import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { qk } from '../lib/queryKeys.js';
import { ChevronDown } from 'lucide-react';
import type { JobCreateInput, ProductLine } from '@gutterinv/shared';
import {
  getCustomers,
  getNextPo,
  postJob,
  getJobTemplates,
} from '../api/client.js';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import { CustomerSearchSelect } from './CustomerSearchSelect.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog.js';
import { formatCents } from '../lib/format.js';

export function CreateJobDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();

  const { data: customers } = useQuery({
    queryKey: ['customers'],
    queryFn: getCustomers,
  });

  const { data: nextPoData } = useQuery({
    queryKey: ['next-po'],
    queryFn: getNextPo,
    enabled: open,
  });

  const { data: templates } = useQuery({
    queryKey: qk.jobs.templates(),
    queryFn: getJobTemplates,
    enabled: open,
  });

  const [customerId, setCustomerId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [address, setAddress] = useState('');
  const [templateId, setTemplateId] = useState('');
  // Product line — no preselection; office must pick before Save can fire.
  // Drives the Gutter / Insulation tabs on the Jobs page.
  const [productLine, setProductLine] = useState<ProductLine | ''>('');

  useEffect(() => {
    if (open) {
      setCustomerId('');
      setDisplayName('');
      // PO field stays empty by default — the server auto-assigns and
      // increments tenants.next_po_number atomically on insert. The
      // current "next" value is shown as a placeholder so the office can
      // preview what they'll get; typing anything overrides it.
      setPoNumber('');
      setAddress('');
      setTemplateId('');
      setProductLine('');
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: postJob,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard', 'today'] });
      onOpenChange(false);
    },
  });

  const handleSave = () => {
    if (!customerId || !displayName.trim() || !address.trim()) return;
    if (!productLine) return; // explicit pick required
    const payload: JobCreateInput = {
      customerId,
      displayName: displayName.trim(),
      address: address.trim(),
      jobType: 'new_work',
      productLine,
      poNumber: poNumber.trim() || null,
      ...(templateId ? { templateId } : {}),
    };
    mutation.mutate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New job</DialogTitle>
          <DialogDescription>
            Create a job for an existing customer. Quote details land later via the
            bot or by direct edit.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label htmlFor="job-customer" className="eyebrow mb-1.5 block">
              Customer *
            </label>
            <CustomerSearchSelect
              id="job-customer"
              value={customerId}
              customers={customers ?? []}
              onChange={setCustomerId}
              disabled={mutation.isPending}
              placeholder="Choose a customer…"
            />
          </div>

          <div>
            <label htmlFor="job-name" className="eyebrow mb-1.5 block">
              Job name *
            </label>
            <Input
              id="job-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Smith residence, Lot 14, Bldg C"
              disabled={mutation.isPending}
            />
          </div>

          <div>
            <label htmlFor="job-address" className="eyebrow mb-1.5 block">
              Site address *
            </label>
            <Input
              id="job-address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="123 Oak St, Anywhere, USA"
              disabled={mutation.isPending}
            />
          </div>

          <div>
            <label className="eyebrow mb-1.5 block">Product line *</label>
            <div className="flex gap-2">
              {(['gutter', 'insulation'] as const).map((pl) => (
                <button
                  key={pl}
                  type="button"
                  onClick={() => setProductLine(pl)}
                  disabled={mutation.isPending}
                  className={
                    'flex-1 rounded-md border px-3 py-2 text-sm font-medium capitalize transition-colors ' +
                    (productLine === pl
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:bg-accent/40')
                  }
                >
                  {pl}
                </button>
              ))}
            </div>
          </div>

          {(templates ?? []).some(
            (t) => t.active && t.fixedPriceCents > 0,
          ) && (
            <div className="rounded-md border border-dashed border-border bg-secondary/20 p-3">
              <label htmlFor="job-template" className="eyebrow mb-1.5 block">
                Manufactured home template (optional)
              </label>
              <div className="relative">
                <select
                  id="job-template"
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                  disabled={mutation.isPending}
                  className="flex h-10 w-full appearance-none rounded-md border border-input bg-background px-3 pr-9 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">Not a template job</option>
                  {(templates ?? [])
                    .filter((t) => t.active && t.fixedPriceCents > 0)
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} — {formatCents(t.fixedPriceCents)}
                      </option>
                    ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-taupe" />
              </div>
              <p className="mt-1 text-[0.6875rem] text-taupe">
                Pins the customer total to the template's price. Line
                items still come from the crew estimate or the office's
                pricing calculator.
              </p>
            </div>
          )}

          <div>
            <label htmlFor="job-po" className="eyebrow mb-1.5 block">
              PO number
            </label>
            <Input
              id="job-po"
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              placeholder={
                nextPoData ? `Auto (${nextPoData.nextPoNumber})` : 'Auto'
              }
              disabled={mutation.isPending}
              className="max-w-xs"
            />
          </div>

          {mutation.error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {mutation.error instanceof Error ? mutation.error.message : 'Save failed'}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              mutation.isPending ||
              !customerId ||
              !displayName.trim() ||
              !address.trim() ||
              !productLine
            }
          >
            {mutation.isPending ? 'Saving…' : 'Create job'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
